// src/services/unitConversionService.js
// =============================================================================
// Frontend API wrapper for Stock / Units of Measure / Unit conversions
// =============================================================================
//
// Unit conversions are maintained by superusers only. They support backend stock
// calculations such as package-aware receipts:
//   10 × Kanister 5 l = 50000 ml
//
// This service intentionally exposes only product-independent conversions such
// as l -> ml or kg -> g. Product-specific package conversion still belongs to
// product master data and stock receipt logic.

import apiClient from "../utils/apiClient";

const DEBUG_API =
  String(process.env.REACT_APP_DEBUG_API || "").toLowerCase().trim() === "true";

function getErrorMessage(err) {
  return (
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.response?.data?.detail ||
    err?.message ||
    "Request failed"
  );
}

function throwApiError(context, err) {
  const message = getErrorMessage(err);
  const e = new Error(`${context}: ${message}`);
  e.status = err?.response?.status;
  e.code = err?.response?.data?.code || err?.code;
  e.data = err?.response?.data;

  if (DEBUG_API) {
    // eslint-disable-next-line no-console
    console.error(`[UnitConversionService] ${context}`, {
      status: e.status,
      code: e.code,
      message,
      data: e.data,
    });
  }

  throw e;
}

function assertId(context, id) {
  if (id === undefined || id === null || `${id}`.trim() === "") {
    const e = new Error(`${context}: id is required.`);
    e.code = "ID_MISSING";
    throw e;
  }
}

async function getList(path, params = {}) {
  try {
    const res = await apiClient.get(path, { withCredentials: true, params });
    return Array.isArray(res?.data) ? res.data : [];
  } catch (err) {
    throwApiError(`GET ${path}`, err);
  }
}

async function post(path, payload) {
  try {
    const res = await apiClient.post(path, payload || {}, {
      withCredentials: true,
      headers: { "Content-Type": "application/json" },
    });
    return res?.data;
  } catch (err) {
    throwApiError(`POST ${path}`, err);
  }
}

async function put(path, payload) {
  try {
    const res = await apiClient.put(path, payload || {}, {
      withCredentials: true,
      headers: { "Content-Type": "application/json" },
    });
    return res?.data;
  } catch (err) {
    throwApiError(`PUT ${path}`, err);
  }
}

async function patch(path, payload) {
  try {
    const res = await apiClient.patch(path, payload || {}, {
      withCredentials: true,
      headers: { "Content-Type": "application/json" },
    });
    return res?.data;
  } catch (err) {
    throwApiError(`PATCH ${path}`, err);
  }
}

export const listUnitConversions = (params = {}) =>
  getList("/stock/unit-conversions", params);

export const createUnitConversion = (payload) =>
  post("/stock/unit-conversions", payload);

export const updateUnitConversion = (id, payload) => {
  assertId("updateUnitConversion", id);
  return put(`/stock/unit-conversions/${id}`, payload);
};

export const setUnitConversionActive = (id, isActive) => {
  assertId("setUnitConversionActive", id);
  return patch(`/stock/unit-conversions/${id}/active`, {
    is_active: Boolean(isActive),
  });
};

export default {
  listUnitConversions,
  createUnitConversion,
  updateUnitConversion,
  setUnitConversionActive,
};
